import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icon from "../shared/Icon";
import { connect } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import { fetchAllOrders, isAbortError } from "../../../services/managerApi";
import { MANAGER_ROUTES } from "../../../utils/managerRouting";
import { buildOverview } from "./aggregate";
import DailyChart from "./DailyChart";
import { STATE_COLORS, stateLabel } from "../shared/StatusPill";
import { ErrorState, LoadingCard } from "../shared/States";
import { formatCurrency, formatLongDate } from "../shared/format";

const Overzicht = ({ t, authUser, history }) => {
  const [orders, setOrders] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchAllOrders({ authUser, signal: controller.signal });
        if (!active) return;
        setOrders(result.orders);
        setTruncated(result.truncated);
      } catch (err) {
        if (!active || isAbortError(err)) return;
        setError(err);
      } finally {
        if (active) setIsLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [authUser, reloadKey]);

  const overview = useMemo(() => buildOverview(orders), [orders]);

  const goToOrders = useCallback(
    (params = {}) => {
      const query = new URLSearchParams(params).toString();
      history.push(`${MANAGER_ROUTES.orders}${query ? `?${query}` : ""}`);
    },
    [history]
  );

  const submitSearch = (event) => {
    event.preventDefault();
    goToOrders(search.trim() ? { q: search.trim() } : {});
  };

  const railTotal = overview.states.reduce((sum, key) => sum + overview.counts[key], 0) || 1;

  return (
    <>
      <div className="mgr-head">
        <h1 className="mgr-h1">{t("Vandaag")}</h1>
        <div className="mgr-date">{formatLongDate(new Date())}</div>
      </div>

      <form onSubmit={submitSearch} style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <div className="mgr-search">
          <Icon name="search" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Zoek een bewoner of kamernummer")}
            aria-label={t("Zoek een bewoner of kamernummer")}
          />
          <button type="submit" className="mgr-btn-pill">
            {t("Zoeken")}
          </button>
        </div>
        <div className="mgr-search-hint">
          {t("Bijvoorbeeld “Peeters” of een bestelnummer — u ziet meteen waar het linnen is.")}
        </div>
      </form>

      {error ? (
        <ErrorState
          message={t("De gegevens konden niet geladen worden.")}
          retryLabel={t("Opnieuw proberen")}
          onRetry={() => setReloadKey((key) => key + 1)}
        />
      ) : null}

      {isLoading ? (
        <>
          <LoadingCard height={210} />
          <LoadingCard height={200} />
        </>
      ) : (
        <>
          <div className="mgr-card mgr-rail">
            <div className="mgr-rail-head">
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: "-.3px" }}>
                  {t("Currently at the laundry")}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--mgr-ink-2)" }}>
                  {overview.atLaundryCount} {t("bestellingen")}
                </div>
              </div>
              <button type="button" className="mgr-link" onClick={() => goToOrders()}>
                {t("Alles bekijken")} →
              </button>
            </div>

            <div className="mgr-rail-bar">
              {overview.states.map((state) => (
                <div
                  key={state}
                  className="mgr-rail-seg"
                  style={{
                    width: `${(overview.counts[state] / railTotal) * 100}%`,
                    background: STATE_COLORS[state]?.bg,
                  }}
                />
              ))}
            </div>

            <div className="mgr-rail-grid">
              {overview.states.map((state) => (
                <button
                  key={state}
                  type="button"
                  className="mgr-stat"
                  onClick={() => goToOrders({ state })}
                >
                  {/* Pas de `fg` ici : ces couleurs sont faites pour le texte
                      POSÉ SUR la pastille, et certaines sont blanches
                      (`ready_for_delivery`, `ready_for_shop`) — sur la carte
                      blanche, le libellé devenait invisible. Le point porte
                      déjà la couleur de l'état. */}
                  <div className="mgr-stat-label">
                    <span className="mgr-dot" style={{ background: STATE_COLORS[state]?.bg }} />
                    {stateLabel(state, t)}
                  </div>
                  <div className="mgr-stat-value">{overview.counts[state]}</div>
                  <div className="mgr-stat-more">{t("Bekijk lijst")} →</div>
                </button>
              ))}
            </div>
          </div>

          <DailyChart
            data={overview.chart}
            t={t}
            onSelectDay={(date) =>
              goToOrders({ pickup_date: date.toISOString().slice(0, 10) })
            }
          />

          <div className="mgr-strip">
            <div style={{ fontSize: 15.5, fontWeight: 600, color: "var(--mgr-ink-2)" }}>
              {t("Kosten deze maand tot vandaag")}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
              <div style={{ fontSize: 20, fontWeight: 800 }}>
                {formatCurrency(overview.monthTotal)}
              </div>
              <button
                type="button"
                className="mgr-link"
                onClick={() => history.push(MANAGER_ROUTES.invoicing)}
              >
                {t("Naar facturatie")} →
              </button>
            </div>
          </div>

          {truncated ? (
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--mgr-ink-3)" }}>
              {t(
                "Cijfers berekend op de recentste bestellingen. Volledige totalen volgen zodra de server ze levert."
              )}
            </div>
          ) : null}
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default compose(
  withRouter,
  withTranslation(),
  connect(mapStateToProps)
)(Overzicht);
